import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';
import AIMartAssistant from '../../components/data/AIMartAssistant';

// Status badge colors
const getStatusColor = (status) => {
  switch (status) {
    case 'ready': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
    case 'error': return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
    case 'generating': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
    case 'deploying': return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
    case 'pending': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  }
};

// Status label for display
const getStatusLabel = (status) => {
  switch (status) {
    case 'ready': return 'Ready';
    case 'error': return 'Error';
    case 'generating': return 'Generating SQL...';
    case 'deploying': return 'Deploying to BigQuery...';
    case 'pending': return 'Pending';
    case 'draft': return 'Draft';
    default: return status;
  }
};

// Aggregation functions for measures
const AGGREGATION_FUNCTIONS = [
  { value: 'SUM', label: 'Sum', template: 'SUM({column})' },
  { value: 'AVG', label: 'Average', template: 'AVG({column})' },
  { value: 'COUNT', label: 'Count', template: 'COUNT({column})' },
  { value: 'COUNT_DISTINCT', label: 'Count Distinct', template: 'COUNT(DISTINCT {column})' },
  { value: 'MIN', label: 'Minimum', template: 'MIN({column})' },
  { value: 'MAX', label: 'Maximum', template: 'MAX({column})' },
  { value: 'CUSTOM', label: 'Custom Expression', template: '' }
];

// Format options for measures
const FORMAT_OPTIONS = [
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'decimal', label: 'Decimal' }
];

// Materialization options
const MATERIALIZATION_OPTIONS = [
  { value: 'view', label: 'View', description: 'Always fresh, computed on query' },
  { value: 'table', label: 'Table', description: 'Pre-computed, faster queries' },
  { value: 'incremental', label: 'Incremental', description: 'Append-only updates' }
];

const MartBuilder = () => {
  const { getCurrentCompanyId, getCurrentCompany } = useAuthStore();
  const companyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  
  const [loading, setLoading] = useState(true);
  const [marts, setMarts] = useState([]);
  const [sources, setSources] = useState({ dataSources: [], wideViews: [] });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMart, setEditingMart] = useState(null);
  const [generatingMarts, setGeneratingMarts] = useState(new Set());
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiGeneratedMart, setAiGeneratedMart] = useState(null);

  // Fetch marts and sources
  const fetchData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const [martsRes, sourcesRes] = await Promise.all([
        apiHelpers.getMarts({ companyId }),
        apiHelpers.getMartSources({ companyId })
      ]);
      
      setMarts(martsRes.data.data || []);
      setSources(sourcesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load marts');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Generate a single mart and deploy to BigQuery
  const handleGenerateMart = async (martId) => {
    try {
      setGeneratingMarts(prev => new Set([...prev, martId]));
      
      // Show loading toast
      const loadingToast = toast.loading('Generating and deploying mart to BigQuery...');
      
      const result = await apiHelpers.generateMart(martId);
      
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      // Handle deployment result
      if (result.data.deployment) {
        const deployment = result.data.deployment;
        
        if (deployment.status === 'success') {
          toast.success('Mart generated and deployed to BigQuery successfully! 🎉');
        } else if (deployment.status === 'in_progress') {
          toast.success('Mart generated! Deployment is still running. Check Dagster UI for status.', {
            duration: 6000
          });
        } else if (deployment.status === 'skipped') {
          toast.success('Mart SQL generated. Deployment skipped (Dagster not available).', {
            duration: 5000
          });
        } else if (deployment.status === 'needs_manual_deploy') {
          toast((t) => (
            <div>
              <p className="font-medium">Mart SQL generated successfully! ✅</p>
              <p className="text-sm mt-1">Manual deployment required:</p>
              <ol className="text-xs mt-2 list-decimal pl-4 space-y-1">
                {deployment.instructions?.slice(0, 2).map((inst, i) => (
                  <li key={i}>{inst}</li>
                ))}
              </ol>
              {deployment.dagsterUrl && (
                <a 
                  href={deployment.dagsterUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:underline mt-2 block"
                >
                  Open Dagster UI →
                </a>
              )}
            </div>
          ), { duration: 10000 });
        } else if (deployment.status === 'failed') {
          toast.error(`Mart generated but deployment failed: ${deployment.error}`, {
            duration: 6000
          });
        }
      } else {
        toast.success('Mart generated successfully');
      }
      
      await fetchData();
    } catch (error) {
      console.error('Failed to generate mart:', error);
      toast.error(error.response?.data?.details || 'Failed to generate mart');
    } finally {
      setGeneratingMarts(prev => {
        const next = new Set(prev);
        next.delete(martId);
        return next;
      });
    }
  };

  // Generate all pending marts
  const handleGenerateAll = async () => {
    try {
      const pendingCount = marts.filter(m => ['draft', 'pending'].includes(m.status)).length;
      if (pendingCount === 0) {
        toast.error('No marts to generate');
        return;
      }
      
      setLoading(true);
      const result = await apiHelpers.generateAllMarts({ companyId });
      toast.success(`Generated ${result.data.generated} marts`);
      await fetchData();
    } catch (error) {
      console.error('Failed to generate marts:', error);
      toast.error('Failed to generate marts');
    } finally {
      setLoading(false);
    }
  };

  // Delete mart
  const handleDeleteMart = async (martId) => {
    if (!window.confirm('Are you sure you want to delete this mart?')) return;
    
    try {
      await apiHelpers.deleteMart(martId);
      toast.success('Mart deleted');
      setMarts(prev => prev.filter(m => m._id !== martId));
    } catch (error) {
      console.error('Failed to delete mart:', error);
      toast.error('Failed to delete mart');
    }
  };

  // Duplicate mart
  const handleDuplicateMart = async (martId) => {
    try {
      const result = await apiHelpers.duplicateMart(martId, {});
      toast.success('Mart duplicated');
      await fetchData();
    } catch (error) {
      console.error('Failed to duplicate mart:', error);
      toast.error(error.response?.data?.error || 'Failed to duplicate mart');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-6 text-center">
          <svg className="mx-auto h-12 w-12 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-amber-800 dark:text-amber-300">No Company Selected</h3>
          <p className="mt-2 text-amber-700 dark:text-amber-400">
            Please select a company from the company selector to manage marts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <svg className="w-7 h-7 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          Mart Builder
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Create data marts with business logic, aggregations, and metrics for analytics.
          {currentCompany && (
            <span className="ml-2 text-purple-600 dark:text-purple-400">
              Company: {currentCompany.name || currentCompany}
            </span>
          )}
        </p>
      </div>

      {/* Actions Bar */}
      <div className="mb-6 flex flex-wrap gap-3">
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Mart
        </button>

        <button
          onClick={() => setShowAIAssistant(true)}
          className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-lg flex items-center gap-2 transition-all shadow-lg hover:shadow-indigo-500/25"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          AI Assistant
        </button>

        <button
          onClick={handleGenerateAll}
          disabled={marts.filter(m => ['draft', 'pending'].includes(m.status)).length === 0 || loading}
          className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-lg flex items-center gap-2 transition-all ml-auto"
        >
          {loading ? (
            <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          )}
          Deploy All Pending
        </button>
      </div>

      {/* Marts List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Data Marts ({marts.length})
          </h2>
        </div>

        {marts.length === 0 ? (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="mt-2">No data marts created yet.</p>
            <p className="text-sm">Click "Create Mart" to define your first business logic mart.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
            >
              Create Your First Mart
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {marts.map((mart) => (
              <div key={mart._id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {mart.displayName || mart.name}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs rounded flex items-center gap-1 ${getStatusColor(mart.status)}`}>
                        {(mart.status === 'generating' || mart.status === 'deploying') && (
                          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                        )}
                        {getStatusLabel(mart.status)}
                      </span>
                      {mart.materialization?.type && (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                          {mart.materialization.type}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {mart.description || 'No description'}
                    </p>

                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                      <span>
                        <strong>Base:</strong> {mart.baseWideView ? `Wide: ${mart.baseWideView}` : mart.baseTable}
                      </span>
                      <span>
                        <strong>Dimensions:</strong> {mart.dimensions?.length || 0}
                      </span>
                      <span>
                        <strong>Measures:</strong> {mart.measures?.length || 0}
                      </span>
                      {mart.dbtModelName && (
                        <span className="text-purple-600 dark:text-purple-400">
                          dbt: {mart.dbtModelName}
                        </span>
                      )}
                    </div>

                    {mart.tags?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {mart.tags.map((tag, i) => (
                          <span key={i} className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {mart.lastError && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                        Error: {mart.lastError}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleGenerateMart(mart._id)}
                      disabled={generatingMarts.has(mart._id) || mart.status === 'deploying'}
                      className="px-3 py-1.5 text-sm bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded flex items-center gap-1.5 transition-all"
                      title="Generate dbt model and deploy to BigQuery"
                    >
                      {generatingMarts.has(mart._id) || mart.status === 'deploying' ? (
                        <>
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                          <span>Deploying...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span>Deploy</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => {
                        setEditingMart(mart);
                        setShowEditModal(true);
                      }}
                      className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      title="Edit mart"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleDuplicateMart(mart._id)}
                      className="p-2 text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
                      title="Duplicate mart"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => handleDeleteMart(mart._id)}
                      className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      title="Delete mart"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Mart Modal */}
      {showCreateModal && (
        <MartFormModal
          mart={aiGeneratedMart}
          sources={sources}
          onClose={() => {
            setShowCreateModal(false);
            setAiGeneratedMart(null);
          }}
          onSave={async (data) => {
            try {
              await apiHelpers.createMart({ ...data, companyId });
              toast.success('Mart created');
              setShowCreateModal(false);
              setAiGeneratedMart(null);
              await fetchData();
            } catch (error) {
              toast.error(error.response?.data?.error || 'Failed to create mart');
            }
          }}
        />
      )}

      {/* Edit Mart Modal */}
      {showEditModal && editingMart && (
        <MartFormModal
          mart={editingMart}
          sources={sources}
          onClose={() => {
            setShowEditModal(false);
            setEditingMart(null);
          }}
          onSave={async (data) => {
            try {
              await apiHelpers.updateMart(editingMart._id, data);
              toast.success('Mart updated');
              setShowEditModal(false);
              setEditingMart(null);
              await fetchData();
            } catch (error) {
              toast.error(error.response?.data?.error || 'Failed to update mart');
            }
          }}
        />
      )}

      {/* AI Mart Assistant */}
      {showAIAssistant && (
        <AIMartAssistant
          companyId={companyId}
          onClose={() => setShowAIAssistant(false)}
          onMartGenerated={(martConfig) => {
            setShowAIAssistant(false);
            setAiGeneratedMart(martConfig);
            setShowCreateModal(true);
          }}
        />
      )}
    </div>
  );
};

// Mart Form Modal Component
const MartFormModal = ({ mart = null, sources, onClose, onSave }) => {
  const isEdit = !!mart;
  
  const [formData, setFormData] = useState({
    name: mart?.name || '',
    displayName: mart?.displayName || '',
    description: mart?.description || '',
    baseTable: mart?.baseTable || '',
    baseWideView: mart?.baseWideView || '',
    dimensions: mart?.dimensions || [],
    measures: mart?.measures || [],
    filters: mart?.filters || [],
    materialization: mart?.materialization || { type: 'table' },
    tags: mart?.tags || [],
    customSql: mart?.customSql || ''
  });

  const [useWideView, setUseWideView] = useState(!!mart?.baseWideView);
  const [showAddDimension, setShowAddDimension] = useState(false);
  const [showAddMeasure, setShowAddMeasure] = useState(false);
  const [showSqlPreview, setShowSqlPreview] = useState(false);
  const [sqlPreview, setSqlPreview] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Get columns from selected source
  const getAvailableColumns = () => {
    if (useWideView && formData.baseWideView) {
      const wv = sources.wideViews.find(v => v.name === formData.baseWideView);
      // Wide views include all columns from base + joined tables + calculated
      return [
        ...(wv?.calculatedColumns?.map(c => ({ name: c.name, type: 'CALCULATED' })) || [])
      ];
    } else if (formData.baseTable) {
      const ds = sources.dataSources.find(d => d.table === formData.baseTable);
      return ds?.columns || [];
    }
    return [];
  };

  // Preview SQL
  const handlePreviewSql = async () => {
    try {
      setPreviewLoading(true);
      const result = await apiHelpers.previewMartSql(formData);
      setSqlPreview(result.data.sql);
      setShowSqlPreview(true);
    } catch (error) {
      console.error('Failed to preview SQL:', error);
      toast.error('Failed to generate SQL preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Add dimension
  const handleAddDimension = (dim) => {
    setFormData(prev => ({
      ...prev,
      dimensions: [...prev.dimensions, dim]
    }));
    setShowAddDimension(false);
  };

  // Add measure
  const handleAddMeasure = (measure) => {
    setFormData(prev => ({
      ...prev,
      measures: [...prev.measures, measure]
    }));
    setShowAddMeasure(false);
  };

  // Add tag
  const handleAddTag = () => {
    if (!newTag.trim()) return;
    setFormData(prev => ({
      ...prev,
      tags: [...new Set([...prev.tags, newTag.trim()])]
    }));
    setNewTag('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit Mart' : 'Create New Mart'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Define dimensions, measures, and business logic for your data mart.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Mart Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase() 
                }))}
                placeholder="e.g., sales_summary"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              />
              <p className="text-xs text-gray-500 mt-1">Lowercase, underscores only</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                placeholder="e.g., Sales Summary"
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe what this mart contains and how it should be used..."
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
            />
          </div>

          {/* Source Selection */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Data Source</h4>
            
            <div className="flex items-center gap-4 mb-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={!useWideView}
                  onChange={() => {
                    setUseWideView(false);
                    setFormData(prev => ({ ...prev, baseWideView: '' }));
                  }}
                  className="text-purple-600"
                />
                <span className="text-sm">Use Data Source</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={useWideView}
                  onChange={() => {
                    setUseWideView(true);
                    setFormData(prev => ({ ...prev, baseTable: '' }));
                  }}
                  className="text-purple-600"
                />
                <span className="text-sm">Use Wide View</span>
              </label>
            </div>

            {useWideView ? (
              <select
                value={formData.baseWideView}
                onChange={(e) => setFormData(prev => ({ ...prev, baseWideView: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              >
                <option value="">Select wide view</option>
                {sources.wideViews.map(wv => (
                  <option key={wv.name} value={wv.name}>
                    {wv.name} (base: {wv.baseTable}, includes: {wv.includedTables?.join(', ')})
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={formData.baseTable}
                onChange={(e) => setFormData(prev => ({ ...prev, baseTable: e.target.value }))}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 dark:bg-gray-700"
              >
                <option value="">Select data source</option>
                {sources.dataSources.map(ds => (
                  <option key={ds._id} value={ds.table}>
                    {ds.name} ({ds.table})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Dimensions */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Dimensions</h4>
                <p className="text-xs text-gray-500">Columns to group by (categories, dates, etc.)</p>
              </div>
              <button
                onClick={() => setShowAddDimension(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
              >
                + Add Dimension
              </button>
            </div>

            {formData.dimensions.length > 0 ? (
              <div className="space-y-2">
                {formData.dimensions.map((dim, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded">
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">{dim.name}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                        ({dim.sourceColumn})
                      </span>
                    </div>
                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        dimensions: prev.dimensions.filter((_, i) => i !== index)
                      }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4 border border-dashed border-gray-300 dark:border-gray-600 rounded">
                No dimensions added yet
              </p>
            )}

            {showAddDimension && (
              <DimensionForm
                columns={getAvailableColumns()}
                onAdd={handleAddDimension}
                onCancel={() => setShowAddDimension(false)}
              />
            )}
          </div>

          {/* Measures */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">Measures</h4>
                <p className="text-xs text-gray-500">Aggregations and calculations (SUM, AVG, COUNT, etc.)</p>
              </div>
              <button
                onClick={() => setShowAddMeasure(true)}
                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg"
              >
                + Add Measure
              </button>
            </div>

            {formData.measures.length > 0 ? (
              <div className="space-y-2">
                {formData.measures.map((measure, index) => (
                  <div key={index} className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded border border-green-200 dark:border-green-800">
                    <div>
                      <span className="font-medium text-green-800 dark:text-green-300">{measure.name}</span>
                      <code className="text-xs text-gray-600 dark:text-gray-400 ml-2 bg-gray-100 dark:bg-gray-700 px-1 rounded">
                        {measure.expression}
                      </code>
                      {measure.format && (
                        <span className="text-xs text-gray-500 ml-2">({measure.format})</span>
                      )}
                    </div>
                    <button
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        measures: prev.measures.filter((_, i) => i !== index)
                      }))}
                      className="text-red-500 hover:text-red-700"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4 border border-dashed border-gray-300 dark:border-gray-600 rounded">
                No measures added yet
              </p>
            )}

            {showAddMeasure && (
              <MeasureForm
                columns={getAvailableColumns()}
                onAdd={handleAddMeasure}
                onCancel={() => setShowAddMeasure(false)}
              />
            )}
          </div>

          {/* Materialization */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Materialization</h4>
            <div className="grid grid-cols-3 gap-3">
              {MATERIALIZATION_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                    formData.materialization?.type === opt.value
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    checked={formData.materialization?.type === opt.value}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      materialization: { ...prev.materialization, type: opt.value }
                    }))}
                    className="hidden"
                  />
                  <div className="font-medium text-gray-900 dark:text-white">{opt.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{opt.description}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Tags</h4>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded flex items-center gap-1"
                >
                  {tag}
                  <button
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      tags: prev.tags.filter((_, i) => i !== index)
                    }))}
                    className="hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder="Add tag..."
                className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm dark:bg-gray-700"
              />
              <button
                onClick={handleAddTag}
                disabled={!newTag.trim()}
                className="px-3 py-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 rounded text-sm"
              >
                Add
              </button>
            </div>
          </div>

          {/* SQL Preview */}
          {showSqlPreview && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900 dark:text-white">Generated SQL Preview</h4>
                <button
                  onClick={() => setShowSqlPreview(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                {sqlPreview}
              </pre>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between sticky bottom-0 bg-white dark:bg-gray-800">
          <button
            onClick={handlePreviewSql}
            disabled={previewLoading || !formData.name || (!formData.baseTable && !formData.baseWideView)}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
          >
            {previewLoading ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            )}
            Preview SQL
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(formData)}
              disabled={!formData.name || (!formData.baseTable && !formData.baseWideView)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg"
            >
              {isEdit ? 'Save Changes' : 'Create Mart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Dimension Form Component
const DimensionForm = ({ columns, onAdd, onCancel }) => {
  const [dim, setDim] = useState({
    name: '',
    sourceColumn: '',
    description: '',
    dataType: 'STRING'
  });

  return (
    <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name</label>
          <input
            type="text"
            value={dim.name}
            onChange={(e) => setDim(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., product_category"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Source Column</label>
          <input
            type="text"
            value={dim.sourceColumn}
            onChange={(e) => setDim(prev => ({ ...prev, sourceColumn: e.target.value }))}
            placeholder="e.g., category"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
          />
        </div>
      </div>
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
        <input
          type="text"
          value={dim.description}
          onChange={(e) => setDim(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Optional description"
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
        />
      </div>
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onCancel} className="px-3 py-1.5 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded">
          Cancel
        </button>
        <button
          onClick={() => onAdd(dim)}
          disabled={!dim.name || !dim.sourceColumn}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded"
        >
          Add Dimension
        </button>
      </div>
    </div>
  );
};

// Measure Form Component
const MeasureForm = ({ columns, onAdd, onCancel }) => {
  const [measure, setMeasure] = useState({
    name: '',
    expression: '',
    description: '',
    format: 'number'
  });
  const [aggFunction, setAggFunction] = useState('SUM');
  const [selectedColumn, setSelectedColumn] = useState('');

  // Auto-generate expression when function or column changes
  const updateExpression = (func, col) => {
    if (func === 'CUSTOM' || !col) return;
    const template = AGGREGATION_FUNCTIONS.find(f => f.value === func)?.template || '';
    const expr = template.replace('{column}', col);
    setMeasure(prev => ({ ...prev, expression: expr }));
  };

  return (
    <div className="mt-3 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Name</label>
          <input
            type="text"
            value={measure.name}
            onChange={(e) => setMeasure(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., total_revenue"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Format</label>
          <select
            value={measure.format}
            onChange={(e) => setMeasure(prev => ({ ...prev, format: e.target.value }))}
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
          >
            {FORMAT_OPTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Aggregation</label>
          <select
            value={aggFunction}
            onChange={(e) => {
              setAggFunction(e.target.value);
              updateExpression(e.target.value, selectedColumn);
            }}
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
          >
            {AGGREGATION_FUNCTIONS.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Column</label>
          <input
            type="text"
            value={selectedColumn}
            onChange={(e) => {
              setSelectedColumn(e.target.value);
              updateExpression(aggFunction, e.target.value);
            }}
            placeholder="e.g., amount"
            className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
          />
        </div>
      </div>
      
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Expression</label>
        <input
          type="text"
          value={measure.expression}
          onChange={(e) => setMeasure(prev => ({ ...prev, expression: e.target.value }))}
          placeholder="e.g., SUM(amount)"
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono dark:bg-gray-700"
        />
      </div>
      
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
        <input
          type="text"
          value={measure.description}
          onChange={(e) => setMeasure(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Optional description"
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm dark:bg-gray-700"
        />
      </div>
      
      <div className="flex justify-end gap-2 mt-3">
        <button onClick={onCancel} className="px-3 py-1.5 text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm rounded">
          Cancel
        </button>
        <button
          onClick={() => onAdd(measure)}
          disabled={!measure.name || !measure.expression}
          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm rounded"
        >
          Add Measure
        </button>
      </div>
    </div>
  );
};

export default MartBuilder;

